'use client';

import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React from 'react';

import { LoginButton } from '@/components/auth/Login';
import { Button } from '@/components/ui/Button';
import { Link } from '@/i18n/routing';

export const CTA: React.FC = () => {
  const t = useTranslations('CtaSection');

  return (
    <section className="bg-section-a relative flex w-full flex-col items-center overflow-hidden px-4 py-[60px] md:px-[80px] md:py-[130px]">
      <div className="z-10 flex max-w-[824px] flex-col items-center gap-[40px] text-center">
        {/* Title and Subtitle */}
        <div className="flex flex-col items-center gap-[20px]">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-matter text-text-primary text-[40px] leading-[48px] font-medium tracking-[-0.035em] md:text-[92px] md:leading-[90px]"
          >
            {t('title')}{' '}
            <span className="text-brand-primary">{t('titleHighlight')}</span>
            <br />
            {t('title2')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-matter text-text-secondary text-[18px] leading-[28px] font-normal tracking-[-0.01em] opacity-80 md:text-[20px] md:leading-[32px]"
          >
            {t('subtext')}
          </motion.p>
        </div>

        {/* Actions and Markers */}
        <div className="flex flex-col items-center gap-[20px]">
          <div className="flex w-full flex-col items-center gap-[16px] sm:w-auto sm:flex-row">
            <LoginButton
              variant="default"
              className="brand-gradient-button h-[51px] w-full cursor-pointer rounded-full text-base font-medium sm:w-[202px]"
              value={t('cta1')}
            >
              <ArrowRight size={18} className="ml-2" />
            </LoginButton>
            <Button
              variant="secondary"
              asChild
              className="border-border-soft bg-surface-card text-text-primary hover:bg-section-b h-[51px] w-full cursor-pointer rounded-full border text-base font-medium shadow-[var(--shadow-card-soft)] sm:w-[163px]"
            >
              <Link href="/contact">{t('cta2')}</Link>
            </Button>
          </div>

          <div className="font-matter text-text-secondary flex flex-wrap items-center justify-center gap-[16px] text-[12px]">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 size={14} />
              <span>{t('footer1')}</span>
            </div>
            <div className="bg-text-secondary hidden h-[11px] w-[1px] opacity-20 sm:block" />
            <div className="flex items-center gap-1.5">
              <Sparkles size={14} />
              <span>{t('footer2')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
